/**
 * Agrupaciones del historial de cierres para los gráficos.
 *
 * Son funciones puras: reciben el historial tal como lo devuelve la API y
 * devuelven datos listos para dibujar. Todo el agrupado se hace en zona
 * Montevideo, no en la del navegador: un cierre de las 23:30 del 31 tiene que
 * caer en ese mes aunque el admin lo mire desde España.
 */

const TZ = 'America/Montevideo';

const MESES_CORTOS = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
export const DIAS_CORTOS = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];

const fmtPartes = new Intl.DateTimeFormat('en-CA', {
  timeZone: TZ,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  weekday: 'short',
});

const DIA_A_INDICE = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };

/** { year, month, day, diaSemana } de un Date, en hora de Montevideo. */
function partesMVD(date) {
  const partes = Object.fromEntries(
    fmtPartes.formatToParts(date).map((p) => [p.type, p.value])
  );
  return {
    year: parseInt(partes.year, 10),
    month: parseInt(partes.month, 10),
    day: parseInt(partes.day, 10),
    diaSemana: DIA_A_INDICE[partes.weekday],
  };
}

/**
 * Total de propinas por mes, para los últimos `cantidad` meses.
 *
 * Devuelve TODOS los meses del rango, incluso los que no tienen cierres: un
 * mes en cero es información (estuvieron cerrados), y si se lo saltea el
 * gráfico miente sobre la continuidad del tiempo.
 *
 * `parcial` marca los meses que no se pueden comparar de igual a igual: el mes
 * en curso (todavía le faltan días) y el primero con datos si el sistema
 * arrancó a mitad de mes. Sin esa marca, la última barra siempre parece una
 * caída de la que nadie tiene la culpa.
 */
export function porMes(history = [], cantidad = 12, ahora = new Date()) {
  const hoy = partesMVD(ahora);

  const totales = new Map();
  let primerMesConDatos = null;
  let primerDiaDelPrimerMes = 32;

  history.forEach((entry) => {
    const f = new Date(entry.fecha);
    if (isNaN(f)) return;
    const { year, month, day } = partesMVD(f);
    const clave = `${year}-${String(month).padStart(2, '0')}`;

    const acum = totales.get(clave) || { total: 0, cierres: 0 };
    acum.total += Number(entry.propina) || 0;
    acum.cierres += 1;
    totales.set(clave, acum);

    if (primerMesConDatos === null || clave < primerMesConDatos) {
      primerMesConDatos = clave;
      primerDiaDelPrimerMes = day;
    } else if (clave === primerMesConDatos && day < primerDiaDelPrimerMes) {
      primerDiaDelPrimerMes = day;
    }
  });

  // El primer mes cuenta como parcial sólo si arrancó pasada la primera semana.
  const primerMesEsParcial = primerDiaDelPrimerMes > 7;

  const salida = [];
  for (let i = cantidad - 1; i >= 0; i--) {
    // Aritmética de meses sobre year/month, sin objetos Date: así no hay
    // sorpresas con los meses de 30/31 días ni con el horario de verano.
    const total = (hoy.year * 12 + (hoy.month - 1)) - i;
    const year = Math.floor(total / 12);
    const month = (total % 12) + 1;
    const clave = `${year}-${String(month).padStart(2, '0')}`;
    const datos = totales.get(clave) || { total: 0, cierres: 0 };
    const esMesActual = year === hoy.year && month === hoy.month;

    salida.push({
      clave,
      etiqueta: `${MESES_CORTOS[month - 1]} ${String(year).slice(2)}`,
      total: datos.total,
      cierres: datos.cierres,
      parcial: esMesActual || (clave === primerMesConDatos && primerMesEsParcial),
      enCurso: esMesActual,
    });
  }
  return salida;
}

/**
 * Cuánto deja un día de semana promedio.
 *
 * Importante: primero se suma por FECHA y después se promedia entre fechas.
 * Si se promediara por cierre, un restaurante que cierra dos veces por día
 * (almuerzo y cena, como hace Fellini) mostraría medio día en cada barra.
 *
 * También devuelve el % sobre el total del período, que es la lectura que le
 * sirve al restaurante: "el fin de semana es el 42% de lo que entra".
 */
export function porDiaSemana(history = []) {
  // fecha -> { diaSemana, total }
  const porFecha = new Map();

  history.forEach((entry) => {
    const f = new Date(entry.fecha);
    if (isNaN(f)) return;
    const { year, month, day, diaSemana } = partesMVD(f);
    const clave = `${year}-${month}-${day}`;
    const acum = porFecha.get(clave) || { diaSemana, total: 0 };
    acum.total += Number(entry.propina) || 0;
    porFecha.set(clave, acum);
  });

  const acumulado = DIAS_CORTOS.map((etiqueta, diaSemana) => ({
    diaSemana,
    etiqueta,
    total: 0,
    dias: 0,
    promedio: 0,
    porcentaje: 0,
  }));

  porFecha.forEach(({ diaSemana, total }) => {
    acumulado[diaSemana].total += total;
    acumulado[diaSemana].dias += 1;
  });

  const totalGeneral = acumulado.reduce((s, d) => s + d.total, 0);
  acumulado.forEach((d) => {
    d.promedio = d.dias > 0 ? Math.round(d.total / d.dias) : 0;
    d.porcentaje = totalGeneral > 0 ? (d.total / totalGeneral) * 100 : 0;
  });

  // Se devuelve empezando el lunes: es como se lee una semana de trabajo.
  return [...acumulado.slice(1), acumulado[0]];
}
